import React, { useMemo } from "react";

import { useChartData, UseChartDataParams } from "@/api/useChartData";

import { LaunchChartCard } from ".";
import { DEFAULT_GROUP_BY, transformLaunchData } from "./helpers";

/**
 * Props for the LaunchChartDataCard component.
 *
 * This interface extends the base chart data parameters to provide
 * launch chart specific configuration.
 */
export interface LaunchChartDataCardProps extends UseChartDataParams {
  /** The title to display on the launch chart card */
  title?: string;
  /** Whether to show the legend - 'auto' shows legend if multiple datasets */
  showLegend?: true | false | "auto";
  /** Maximum speed value for the chart */
  maxSpeed?: number;
  /** Unit of measurement for the chart */
  unit?: string;
  /** Children elements to render in the chart */
  children?: React.ReactNode;
}

/**
 * LaunchChartDataCard Component
 *
 * A data-driven launch chart visualization component that fetches real player data
 * and displays it as a radar-style launch angle and exit velocity plot for baseball analytics.
 *
 * Features:
 * - Fetches live player chart data from the API
 * - Transforms backend data into launch angle/speed format
 * - Shows loading states and error handling
 * - Supports various filters (result type, pitch type, etc.)
 *
 * @param props - Configuration for the launch chart including data parameters and display options
 * @returns A rendered launch chart card with player data
 *
 * @example
 * ```tsx
 * <LaunchChartDataCard
 *   title="Launch VS Exit Velocity"
 *   playerExternalId="player-123"
 *   startDate="2024-01-01"
 *   endDate="2024-12-31"
 *   gameType="game"
 *   playerType="hitter"
 *   vsHandedness="all"
 *   chartType="launch"
 *   maxSpeed={115}
 *   unit="MPH"
 * />
 * ```
 */
export const LaunchChartDataCard: React.FC<LaunchChartDataCardProps> = ({
  title = "Launch",
  showLegend = "auto",
  maxSpeed = 115,
  unit = "MPH",
  children,
  playerExternalId,
  startDate,
  endDate,
  gameType,
  playerType,
  vsHandedness,
  chartType,
  resultType,
  pitchType,
  filters,
  groupBy,
}) => {
  const { data, isLoading, error } = useChartData({
    playerExternalId,
    startDate,
    endDate,
    gameType,
    playerType,
    vsHandedness,
    chartType,
    resultType,
    pitchType,
    filters,
    hitsAndOutsOnly: true,
    groupBy: groupBy || DEFAULT_GROUP_BY,
  });

  const datasets = useMemo(() => {
    return transformLaunchData(data, groupBy || DEFAULT_GROUP_BY);
  }, [data, groupBy]);

  if (error) {
    return (
      <div className="w-full min-h-[330px] h-full flex items-center justify-center bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-500">Unable to load chart data</p>
      </div>
    );
  }

  return (
    <LaunchChartCard
      title={title}
      datasets={datasets}
      showLegend={showLegend}
      maxSpeed={maxSpeed}
      unit={unit}
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg">
          <div className="text-sm text-gray-500">Loading...</div>
        </div>
      )}
      {children}
    </LaunchChartCard>
  );
};
